import { clamp } from './utils.js';
import { ensureAudio, unlockAudioFromGesture, playUiTick, getAudioCtx } from './audio.js';

/**
 * @typedef {object} ControlsDeps
 * @property {any} el
 * @property {{ buildBeatLights: (beats: number) => void, logLine: (msg: string) => void }} ui
 * @property {{
 * bpm: number,
 * beatsPerBar: number,
 * subdivision: number,
 * swingPct: number,
 * downbeatEnabled: boolean,
 * secondaryPulses: number,
 * secondaryRounds: number,
 * secondaryMode: number,
 * primaryPulses: number,
 * primaryRounds: number,
 * primaryMode: number,
 * primaryStressVolPct: number,
 * secondaryStressVolPct: number,
 * unstressedVolPct: number,
 * primaryPitchHz: number,
 * secondaryPitchHz: number,
 * unstressedPitchHz: number,
 * primaryDecayMs: number,
 * secondaryDecayMs: number,
 * unstressedDecayMs: number
 * }} state
 * @property {(bpm: number) => void} setBpm
 * @property {(pct: number) => void} setSwing
 * @property {(db: number) => void} setVolumeDb
 * @property {() => void} updateRhythm
 * @property {() => void} reset
 * @property {() => void} toggleStartStop
 * @property {() => void} exportMidi
 * @property {() => void} clearPresetSelection
 * @property {() => void} applyReverse
 * @property {() => void} applyFlip
 * @property {() => void} applyInvertSecondary
 * @property {(steps: number, pulses: number) => number} maxRoundCount
 * @property {(steps: number, pulses: number, numRounds?: number) => number} modeCount
 */

/** @param {ControlsDeps} deps */
export function initControls({
    el,
    ui,
    state,
    setBpm,
    setSwing,
    setVolumeDb,
    updateRhythm,
    reset,
    toggleStartStop,
    exportMidi,
    clearPresetSelection,
    applyReverse,
    applyFlip,
    applyInvertSecondary,
    maxRoundCount,
    modeCount,
}) {
    /** @param {HTMLElement | null} target */
    function preventDoubleTapZoom(target) {
        if (!target) return;
        let lastTap = 0;
        target.addEventListener(
            'touchend',
            (e) => {
                const now = Date.now();
                if (now - lastTap < 300) {
                    e.preventDefault();
                }
                lastTap = now;
            },
            { passive: false },
        );
    }

    /**
     * @param {HTMLElement | null} target
     * @param {() => void} handler
     * @param {{ tick?: boolean }} [opts]
     */
    function fastTap(target, handler, { tick = true } = {}) {
        if (!target) return;
        let suppressClick = false;
        target.addEventListener(
            'touchstart',
            (e) => {
                suppressClick = true;
                e.preventDefault();
                if (tick) playUiTick();
                handler();
            },
            { passive: false },
        );
        target.addEventListener('click', (e) => {
            if (suppressClick) {
                e.preventDefault();
                suppressClick = false;
                return;
            }
            if (tick) playUiTick();
            handler();
        });
    }

    /**
     * @param {HTMLButtonElement | null} target
     * @param {() => void} handler
     * @param {{ initialDelay?: number, repeatInterval?: number }} [opts]
     */
    function bindHoldRepeat(target, handler, { initialDelay = 350, repeatInterval = 160 } = {}) {
        if (!target) return;
        /** @type {number | null} */
        let holdTimer = null;
        /** @type {number | null} */
        let repeatTimer = null;
        let suppressClick = false;

        const stopRepeat = () => {
            if (holdTimer) {
                window.clearTimeout(holdTimer);
                holdTimer = null;
            }
            if (repeatTimer) {
                window.clearInterval(repeatTimer);
                repeatTimer = null;
            }
        };

        const trigger = () => {
            if (target.disabled) return;
            playUiTick();
            handler();
        };

        target.addEventListener(
            'pointerdown',
            (e) => {
                if (target.disabled) return;
                suppressClick = true;
                e.preventDefault();
                unlockAudioFromGesture();
                trigger();
                holdTimer = window.setTimeout(() => {
                    repeatTimer = window.setInterval(trigger, repeatInterval);
                }, initialDelay);
            },
            { passive: false },
        );

        target.addEventListener('pointerup', stopRepeat);
        target.addEventListener('pointercancel', stopRepeat);
        target.addEventListener('pointerleave', stopRepeat);
        target.addEventListener('lostpointercapture', stopRepeat);

        target.addEventListener('click', (e) => {
            if (suppressClick) {
                e.preventDefault();
                suppressClick = false;
                return;
            }
            trigger();
        });
    }

    let bpmDraftFresh = false;

    function commitBpmInput() {
        const v = parseFloat(el.bpmNumber.value);
        if (!Number.isFinite(v)) {
            el.bpmNumber.value = String(state.bpm);
            return;
        }
        setBpm(v);
    }

    el.bpmNumber.addEventListener('focus', () => {
        bpmDraftFresh = true;
        el.bpmNumber.select();
    });
    el.bpmNumber.addEventListener('input', () => {
        bpmDraftFresh = false;
    });
    el.bpmNumber.addEventListener('change', commitBpmInput);
    el.bpmNumber.addEventListener('blur', commitBpmInput);
    el.bpmNumber.addEventListener(
        'keydown',
        /** @param {KeyboardEvent} e */ (e) => {
            if (bpmDraftFresh && /^[0-9]$/.test(e.key)) {
                el.bpmNumber.value = '';
                bpmDraftFresh = false;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setBpm(state.bpm + 1);
                return;
            }
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setBpm(state.bpm - 1);
                return;
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                el.bpmNumber.value = String(state.bpm);
                bpmDraftFresh = false;
                return;
            }
            if (e.key !== 'Enter') return;
            e.preventDefault();
            bpmDraftFresh = false;
            commitBpmInput();
        },
    );
    bindHoldRepeat(el.bpmDown, () => setBpm(state.bpm - 1));
    bindHoldRepeat(el.bpmUp, () => setBpm(state.bpm + 1));
    preventDoubleTapZoom(el.bpmDown);
    preventDoubleTapZoom(el.bpmUp);

    el.beatsPerBar.addEventListener('change', () => {
        state.beatsPerBar = parseInt(el.beatsPerBar.value, 10);
        ui.buildBeatLights(state.beatsPerBar);
        updateRhythm();
        reset();
        clearPresetSelection();
    });

    el.subdivision.addEventListener('change', () => {
        state.subdivision = parseInt(el.subdivision.value, 10);
        updateRhythm();
        reset();
        clearPresetSelection();
    });

    el.downbeatBtn.addEventListener('click', () => {
        playUiTick();
        state.downbeatEnabled = !state.downbeatEnabled;
        el.downbeatBtn.textContent = 'Downbeat';
        el.downbeatBtn.classList.toggle('primary', state.downbeatEnabled);
        el.downbeatBtn.classList.toggle('isOn', state.downbeatEnabled);
        el.downbeatBtn.classList.toggle('debossed', state.downbeatEnabled);
        el.downbeatBtn.classList.toggle('debossedText', state.downbeatEnabled);
        el.downbeatBtn.classList.remove('danger');
        ui.logLine(`Downbeat tone ${state.downbeatEnabled ? 'ON' : 'OFF'}`);
    });

    el.secondaryPulsesNumber.addEventListener('input', () => {
        const v = parseInt(el.secondaryPulsesNumber.value, 10);
        state.secondaryPulses = Number.isNaN(v) ? 0 : v;
        const steps = state.beatsPerBar * state.subdivision;
        state.secondaryRounds = maxRoundCount(steps, state.secondaryPulses);
        updateRhythm();
        clearPresetSelection();
    });

    el.secondaryRoundsNumber.addEventListener('input', () => {
        const v = parseInt(el.secondaryRoundsNumber.value, 10);
        state.secondaryRounds = Number.isNaN(v) ? 0 : v;
        updateRhythm();
        clearPresetSelection();
    });

    /** @param {HTMLInputElement} inputEl */
    function getRoundMax(inputEl) {
        if (inputEl.id === 'secondaryRoundsNumber') {
            const steps = state.beatsPerBar * state.subdivision;
            return Math.max(0, maxRoundCount(steps, state.secondaryPulses));
        }
        if (inputEl.id === 'primaryRoundsNumber') {
            return Math.max(0, maxRoundCount(state.secondaryPulses, state.primaryPulses));
        }
        return parseInt(inputEl.max, 10);
    }

    /** @param {HTMLInputElement} inputEl */
    function getModeMax(inputEl) {
        if (inputEl.id === 'secondaryModeNumber') {
            const steps = state.beatsPerBar * state.subdivision;
            return Math.max(1, modeCount(steps, state.secondaryPulses, state.secondaryRounds));
        }
        if (inputEl.id === 'primaryModeNumber') {
            return Math.max(
                1,
                modeCount(state.secondaryPulses, state.primaryPulses, state.primaryRounds),
            );
        }
        if (inputEl.id === 'secondaryRoundsNumber' || inputEl.id === 'primaryRoundsNumber') {
            return getRoundMax(inputEl);
        }
        return parseInt(inputEl.max, 10);
    }

    /** @param {HTMLInputElement} inputEl @param {number} val */
    function wrapModeValue(inputEl, val) {
        const min = parseInt(inputEl.min, 10);
        const max = getModeMax(inputEl);
        const safeMin = Number.isFinite(min) ? min : 0;
        if (!Number.isFinite(max)) return clamp(val, safeMin, safeMin);
        if (val > max) return safeMin;
        if (val < safeMin) return max;
        return val;
    }

    /** @param {HTMLInputElement} inputEl @param {number} delta */
    function stepNumericInput(inputEl, delta) {
        const min = parseInt(inputEl.min, 10);
        const max = getModeMax(inputEl);
        const cur = parseInt(inputEl.value, 10) || 0;
        const wrap = inputEl.dataset['wrap'] === 'true';

        let next = cur + delta;
        const safeMin = Number.isFinite(min) ? min : 0;
        const safeMax = Number.isFinite(max) ? max : safeMin;

        if (wrap) {
            if (next > safeMax) next = safeMin;
            if (next < safeMin) next = safeMax;
        } else {
            next = clamp(next, safeMin, safeMax);
        }

        inputEl.value = String(next);

        if (inputEl.id === 'secondaryModeNumber') state.secondaryMode = next - 1;
        if (inputEl.id === 'primaryModeNumber') state.primaryMode = next - 1;
        if (inputEl.id === 'secondaryPulsesNumber') state.secondaryPulses = next;
        if (inputEl.id === 'primaryPulsesNumber') state.primaryPulses = next;
        if (inputEl.id === 'secondaryRoundsNumber') state.secondaryRounds = next;
        if (inputEl.id === 'primaryRoundsNumber') state.primaryRounds = next;

        updateRhythm();
    }

    /** @param {HTMLSelectElement} selectEl @param {number} delta */
    function stepSelect(selectEl, delta) {
        const enabledOptions = Array.from(selectEl.options).filter((opt) => !opt.disabled);
        if (enabledOptions.length === 0) return;
        let curIdx = enabledOptions.findIndex((opt) => opt.value === selectEl.value);
        if (curIdx === -1) {
            curIdx = enabledOptions.findIndex((opt) => opt.selected);
        }
        if (curIdx === -1) curIdx = 0;
        let nextIdx = curIdx + delta;
        if (nextIdx < 0) nextIdx = enabledOptions.length - 1;
        if (nextIdx >= enabledOptions.length) nextIdx = 0;
        const nextOpt = enabledOptions[nextIdx];
        if (!nextOpt) return;
        selectEl.value = nextOpt.value;
        selectEl.dispatchEvent(new Event('change', { bubbles: true }));
        selectEl.focus();
    }

    el.secondaryModeNumber.addEventListener('input', () => {
        let v = parseInt(el.secondaryModeNumber.value, 10);
        if (Number.isNaN(v)) v = 1;
        v = wrapModeValue(el.secondaryModeNumber, v);
        el.secondaryModeNumber.value = String(v);
        state.secondaryMode = v - 1;
        updateRhythm();
        clearPresetSelection();
    });
    el.secondaryModeNumber.addEventListener(
        'wheel',
        /** @param {WheelEvent} e */ (e) => {
            e.preventDefault();
            stepNumericInput(el.secondaryModeNumber, e.deltaY < 0 ? 1 : -1);
            clearPresetSelection();
        },
    );
    el.secondaryModeNumber.addEventListener(
        'keydown',
        /** @param {KeyboardEvent} e */ (e) => {
            if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
            const max = getModeMax(el.secondaryModeNumber);
            const cur = parseInt(el.secondaryModeNumber.value, 10) || 0;
            if (e.key === 'ArrowUp' && cur >= max) {
                e.preventDefault();
                el.secondaryModeNumber.value = '1';
                state.secondaryMode = 0;
                updateRhythm();
                clearPresetSelection();
            }
            if (e.key === 'ArrowDown' && cur <= 1) {
                e.preventDefault();
                el.secondaryModeNumber.value = String(max);
                state.secondaryMode = max - 1;
                updateRhythm();
                clearPresetSelection();
            }
        },
    );

    el.primaryPulsesNumber.addEventListener('input', () => {
        state.primaryPulses = parseInt(el.primaryPulsesNumber.value, 10) || 0;
        state.primaryRounds = maxRoundCount(state.secondaryPulses, state.primaryPulses);
        updateRhythm();
        clearPresetSelection();
    });

    el.primaryRoundsNumber.addEventListener('input', () => {
        state.primaryRounds = parseInt(el.primaryRoundsNumber.value, 10) || 0;
        updateRhythm();
        clearPresetSelection();
    });

    el.primaryStressVolRange.addEventListener('input', () => {
        state.primaryStressVolPct = parseInt(el.primaryStressVolRange.value, 10) || 0;
        el.primaryStressVolLabel.textContent = String(state.primaryStressVolPct);
    });

    el.primaryModeNumber.addEventListener('input', () => {
        let v = parseInt(el.primaryModeNumber.value, 10);
        if (Number.isNaN(v)) v = 1;
        v = wrapModeValue(el.primaryModeNumber, v);
        el.primaryModeNumber.value = String(v);
        state.primaryMode = v - 1;
        updateRhythm();
        clearPresetSelection();
    });
    el.primaryModeNumber.addEventListener(
        'wheel',
        /** @param {WheelEvent} e */ (e) => {
            e.preventDefault();
            stepNumericInput(el.primaryModeNumber, e.deltaY < 0 ? 1 : -1);
            clearPresetSelection();
        },
    );
    el.primaryModeNumber.addEventListener(
        'keydown',
        /** @param {KeyboardEvent} e */ (e) => {
            if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
            const max = getModeMax(el.primaryModeNumber);
            const cur = parseInt(el.primaryModeNumber.value, 10) || 0;
            if (e.key === 'ArrowUp' && cur >= max) {
                e.preventDefault();
                el.primaryModeNumber.value = '1';
                state.primaryMode = 0;
                updateRhythm();
                clearPresetSelection();
            }
            if (e.key === 'ArrowDown' && cur <= 1) {
                e.preventDefault();
                el.primaryModeNumber.value = String(max);
                state.primaryMode = max - 1;
                updateRhythm();
                clearPresetSelection();
            }
        },
    );

    // Pitch accent (stress-level base frequencies)
    el.primaryPitchRange.addEventListener('input', () => {
        state.primaryPitchHz = clamp(parseInt(el.primaryPitchRange.value, 10) || 200, 200, 2000);
        el.primaryPitchLabel.textContent = String(state.primaryPitchHz);
    });
    el.secondaryPitchRange.addEventListener('input', () => {
        state.secondaryPitchHz = clamp(
            parseInt(el.secondaryPitchRange.value, 10) || 200,
            200,
            2000,
        );
        el.secondaryPitchLabel.textContent = String(state.secondaryPitchHz);
    });
    el.unstressedPitchRange.addEventListener('input', () => {
        state.unstressedPitchHz = clamp(
            parseInt(el.unstressedPitchRange.value, 10) || 200,
            200,
            2000,
        );
        el.unstressedPitchLabel.textContent = String(state.unstressedPitchHz);
    });

    // Duration accent (stress-level base decays)
    el.primaryDecayRange.addEventListener('input', () => {
        state.primaryDecayMs = clamp(parseInt(el.primaryDecayRange.value, 10) || 5, 5, 500);
        el.primaryDecayLabel.textContent = String(state.primaryDecayMs);
    });
    el.secondaryDecayRange.addEventListener('input', () => {
        state.secondaryDecayMs = clamp(parseInt(el.secondaryDecayRange.value, 10) || 5, 5, 500);
        el.secondaryDecayLabel.textContent = String(state.secondaryDecayMs);
    });
    el.unstressedDecayRange.addEventListener('input', () => {
        state.unstressedDecayMs = clamp(parseInt(el.unstressedDecayRange.value, 10) || 5, 5, 500);
        el.unstressedDecayLabel.textContent = String(state.unstressedDecayMs);
    });

    document.querySelectorAll('.modeBtn').forEach((btn) => {
        btn.addEventListener('click', () => {
            if (btn.hasAttribute('data-select-target')) return;
            const targetId = btn.getAttribute('data-target');
            if (!targetId) return;
            const delta = parseInt(btn.getAttribute('data-delta') || '0', 10) || 0;
            const inputEl = document.getElementById(targetId);
            if (!(inputEl instanceof HTMLInputElement)) return;
            stepNumericInput(inputEl, delta);
            clearPresetSelection();
        });
    });

    document.querySelectorAll('[data-select-target]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const targetId = btn.getAttribute('data-select-target');
            if (!targetId) return;
            const delta = parseInt(btn.getAttribute('data-delta') || '0', 10) || 0;
            const selectEl = document.getElementById(targetId);
            if (!(selectEl instanceof HTMLSelectElement)) return;
            stepSelect(selectEl, delta);
        });
    });

    el.unstressedVolRange.addEventListener('input', () => {
        state.unstressedVolPct = parseInt(el.unstressedVolRange.value, 10) || 0;
        el.unstressedVolLabel.textContent = String(state.unstressedVolPct);
    });
    el.secondaryStressVolRange.addEventListener('input', () => {
        state.secondaryStressVolPct = parseInt(el.secondaryStressVolRange.value, 10) || 0;
        el.secondaryStressVolLabel.textContent = String(state.secondaryStressVolPct);
    });

    el.copyPatternBtn.addEventListener('click', async () => {
        const text = el.rhythmStringDisplay.textContent || '';
        try {
            await navigator.clipboard.writeText(text);
            const prev = el.copyPatternBtn.textContent;
            el.copyPatternBtn.textContent = 'Copied!';
            window.setTimeout(() => {
                el.copyPatternBtn.textContent = prev;
            }, 900);
        } catch (err) {
            ui.logLine('Copy failed (clipboard permission).');
        }
    });

    el.exportMidiBtn.addEventListener('click', () => {
        exportMidi();
        ui.logLine('Export: MIDI loop');
    });

    if (el.reverseBtn) {
        el.reverseBtn.addEventListener('click', () => {
            applyReverse();
            ui.logLine('Transform: Reverse');
            clearPresetSelection();
        });
    }
    if (el.flipBtn) {
        el.flipBtn.addEventListener('click', () => {
            applyFlip();
            ui.logLine('Transform: Flip');
            clearPresetSelection();
        });
    }
    if (el.invertBtn) {
        el.invertBtn.addEventListener('click', () => {
            applyInvertSecondary();
            ui.logLine('Transform: Invert secondary');
            clearPresetSelection();
        });
    }

    if (el.swingPill) {
        let dragStartY = 0;
        let dragStartVal = 0;
        let dragging = false;

        /** @param {PointerEvent} e */
        const onMove = (e) => {
            if (!dragging) return;
            const delta = (dragStartY - e.clientY) / 1.2;
            setSwing(dragStartVal + delta);
        };

        const onUp = () => {
            dragging = false;
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };

        el.swingPill.addEventListener(
            'pointerdown',
            /** @param {PointerEvent} e */ (e) => {
                e.preventDefault();
                dragging = true;
                dragStartY = e.clientY;
                dragStartVal = state.swingPct;
                el.swingPill.setPointerCapture(e.pointerId);
                window.addEventListener('pointermove', onMove);
                window.addEventListener('pointerup', onUp);
            },
        );
    }

    if (el.volPill) {
        let dragStartY = 0;
        let dragStartVal = -10;
        let dragging = false;

        /** @param {PointerEvent} e */
        const onMove = (e) => {
            if (!dragging) return;
            const delta = (dragStartY - e.clientY) / 2.0;
            setVolumeDb(dragStartVal + delta);
        };

        const onUp = () => {
            dragging = false;
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };

        el.volPill.addEventListener(
            'pointerdown',
            /** @param {PointerEvent} e */ (e) => {
                e.preventDefault();
                dragging = true;
                dragStartY = e.clientY;
                dragStartVal = parseInt(el.volLabel.textContent, 10) || -10;
                el.volPill.setPointerCapture(e.pointerId);
                window.addEventListener('pointermove', onMove);
                window.addEventListener('pointerup', onUp);
            },
        );
    }

    fastTap(el.startStopBtn, () => toggleStartStop(), { tick: false });
    fastTap(el.resetBtn, () => reset());
    preventDoubleTapZoom(el.startStopBtn);
    preventDoubleTapZoom(el.resetBtn);

    el.toggleLogBtn.textContent = 'Log';
    el.toggleLogBtn.addEventListener('click', () => {
        const isOpen = el.logWrap.classList.toggle('isOpen');
        el.toggleLogBtn.textContent = isOpen ? 'Hide log' : 'Log';
    });

    /** @param {HTMLDetailsElement | null} detailsEl */
    function wireChev(detailsEl) {
        if (!detailsEl) return;
        const summary = detailsEl.querySelector('summary');
        const chev = detailsEl.querySelector('.chev');
        const refresh = () => {
            if (!chev) return;
            chev.textContent = detailsEl.open ? '▾' : '▸';
        };
        refresh();
        detailsEl.addEventListener('toggle', refresh);
        if (summary) summary.addEventListener('click', () => window.setTimeout(refresh, 0));
    }

    wireChev(/** @type {HTMLDetailsElement | null} */ (document.getElementById('advDetails')));
    wireChev(/** @type {HTMLDetailsElement | null} */ (document.getElementById('volumeDetails')));
    wireChev(/** @type {HTMLDetailsElement | null} */ (document.getElementById('pitchDetails')));
    wireChev(/** @type {HTMLDetailsElement | null} */ (document.getElementById('envelopeDetails')));

    /** @type {(HTMLDetailsElement | null)[]} */
    const accentDetails = [
        /** @type {HTMLDetailsElement | null} */ (document.getElementById('advDetails')),
        /** @type {HTMLDetailsElement | null} */ (document.getElementById('volumeDetails')),
        /** @type {HTMLDetailsElement | null} */ (document.getElementById('pitchDetails')),
        /** @type {HTMLDetailsElement | null} */ (document.getElementById('envelopeDetails')),
    ];
    const activeAccentDetails = accentDetails.filter((d) => d !== null);

    activeAccentDetails.forEach((d) => {
        d.addEventListener('toggle', () => {
            if (!d.open) return;
            for (const other of activeAccentDetails) {
                if (other !== d) other.open = false;
            }
        });
    });

    /** @type {number[]} */
    const tapTimes = [];
    const TAP_WINDOW = 6;

    function tapTempo() {
        ensureAudio();
        unlockAudioFromGesture();

        const now = performance.now();
        tapTimes.push(now);
        while (tapTimes.length > TAP_WINDOW) tapTimes.shift();

        if (tapTimes.length >= 2) {
            let sum = 0;
            for (let i = 1; i < tapTimes.length; i++) {
                const prev = tapTimes[i - 1];
                const curr = tapTimes[i];
                if (prev === undefined || curr === undefined) continue;
                sum += curr - prev;
            }
            const avgMs = sum / (tapTimes.length - 1);
            const newBpm = 60000 / avgMs;
            setBpm(newBpm);
            ui.logLine(`Tap tempo → ${Math.round(newBpm)} BPM`);
        } else {
            ui.logLine('Tap…');
        }
    }

    fastTap(el.tapBtn, tapTempo);
    el.tapBtn.addEventListener('pointerdown', unlockAudioFromGesture);
    preventDoubleTapZoom(el.tapBtn);

    if (el.audioEnableBtn) {
        el.audioEnableBtn.addEventListener('click', () => {
            unlockAudioFromGesture();
            const audioCtx = getAudioCtx();
            if (audioCtx) {
                audioCtx.resume().catch(() => {});
            }
        });
    }

    window.addEventListener('keydown', (e) => {
        const tag = e.target instanceof Element ? e.target.tagName.toLowerCase() : '';
        if (tag === 'input' || tag === 'select' || tag === 'textarea') return;

        if (e.code === 'Space') {
            e.preventDefault();
            toggleStartStop();
        }
        if (e.key.toLowerCase() === 't') tapTempo();
        if (e.key.toLowerCase() === 'r') reset();
    });

    ['pointerdown', 'touchstart', 'touchend'].forEach((evt) => {
        window.addEventListener(evt, unlockAudioFromGesture, { once: true, passive: true });
    });
}
